<template>
  <div class="sda">
    <!-- ── Badges ──────────────────────────────────────────────── -->
    <section class="sda-section">
      <header class="sda-section__head">
        <div>
          <h2 class="sda-section__title">Badges</h2>
          <p class="sda-section__sub">Earned by completing a pathway module, and a pathway badge for finishing every module.</p>
        </div>
        <div v-if="badges.length" class="sda-filter" role="tablist" aria-label="Badge type">
          <button v-for="opt in filterOptions" :key="opt.value" type="button" role="tab" :aria-selected="filter === opt.value ? 'true' : 'false'"
            class="sda-filter__tab" :class="{ 'is-active': filter === opt.value }" @click="filter = opt.value">
            {{ opt.label }} <span class="sda-filter__count">{{ countFor(opt.value) }}</span>
          </button>
        </div>
      </header>

      <div v-if="loadingBadges" class="sda-empty"><span class="sda-spinner" aria-label="Loading badges"></span></div>
      <div v-else-if="badges.length === 0" class="sda-empty">No badges yet — complete a module in one of your pathways to earn your first.</div>
      <div v-else-if="filteredBadges.length === 0" class="sda-empty">No {{ filter }} badges yet.</div>
      <div v-else class="sda-grid">
        <article v-for="b in filteredBadges" :key="'b-' + b.id" class="sda-badge" :class="'sda-badge--' + b.type">
          <div class="sda-medal" aria-hidden="true">
            <svg viewBox="0 0 100 100" class="sda-medal__svg">
              <circle cx="50" cy="50" r="46" fill="none" :stroke="b.type === 'pathway' ? '#1e3a5f' : '#3e7799'" stroke-width="2.5" />
              <circle cx="50" cy="50" r="40" :fill="b.type === 'pathway' ? '#1e3a5f' : '#3e7799'" />
              <circle cx="50" cy="50" r="33" fill="none" stroke="rgba(255,255,255,0.35)" stroke-width="1.5" stroke-dasharray="3 3" />
              <g v-if="b.type === 'pathway'" fill="#fff">
                <path d="M50 26l6.5 13.2 14.5 2.1-10.5 10.2 2.5 14.5L50 59.2 37 66l2.5-14.5L29 41.3l14.5-2.1z" />
              </g>
              <g v-else fill="none" stroke="#fff" stroke-width="5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M33 51l11 11 23-24" />
              </g>
            </svg>
          </div>
          <div class="sda-badge__body">
            <span class="sda-pill" :class="'sda-pill--' + b.type">{{ b.type === 'pathway' ? 'Pathway badge' : 'Module badge' }}</span>
            <h3 class="sda-badge__title">{{ b.subject }}</h3>
            <p v-if="b.pathway" class="sda-badge__path">
              <router-link v-if="b.pathway_id" :to="'/safedig/pathways/' + b.pathway_id" class="sda-link">{{ b.pathway }}</router-link>
              <span v-else>{{ b.pathway }}</span>
            </p>
            <p class="sda-badge__date">Earned {{ formatDate(b.earned_at) }}</p>
          </div>
        </article>
      </div>
    </section>
  </div>
</template>

<script>
/**
 * Badge gallery for the Certificates page, Safe Dig only. Follows GLAD's
 * BadgeGallery (filter tabs, medal cards, type pill, pathway breadcrumb);
 * the data comes from Safe Dig's own badge table rather than GLAD's
 * name-prefixed certificate rows. Pathway completion is shown here as the
 * pathway badge; the certificate record itself is not listed separately.
 */
export default {
  name: 'SafeDigAchievements',
  data() {
    return {
      loadingBadges: true,
      badges: [],
      filter: 'all',
      filterOptions: [
        { value: 'all', label: 'All' },
        { value: 'module', label: 'Module' },
        { value: 'pathway', label: 'Pathway' },
      ],
    };
  },
  computed: {
    filteredBadges() {
      return this.filter === 'all' ? this.badges : this.badges.filter(b => b.type === this.filter);
    },
  },
  created() {
    this.$http.get('safedig/badges')
      .then(resp => { this.badges = (resp.data && resp.data.data) || []; })
      .catch(() => { this.badges = []; })
      .finally(() => { this.loadingBadges = false; });
  },
  methods: {
    countFor(type) { return type === 'all' ? this.badges.length : this.badges.filter(b => b.type === type).length; },
    formatDate(v) {
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return v || '';
      return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    },
  },
};
</script>

<style scoped>
.sda {
  --sd-accent: #3e7799;
  --sd-accent-dark: #2d5a73;
  --sd-accent-soft: rgba(62, 119, 153, 0.14);
  --sd-navy: #1e3a5f;
  --sd-text: #0f172a;
  --sd-muted: #475569;
  --sd-border: #e2e8f0;
  --sd-radius: 12px;
  --sd-shadow: 0 1px 2px rgba(15, 23, 42, 0.06), 0 6px 16px rgba(15, 23, 42, 0.05);
  margin-bottom: 1.5rem;
}
.sda-section { margin-bottom: 1.75rem; }
.sda-section__head { display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem; margin-bottom: 0.9rem; flex-wrap: wrap; }
.sda-section__title { font-size: 1.15rem; font-weight: 700; color: var(--sd-navy); margin: 0 0 0.2rem; }
.sda-section__sub { font-size: 0.85rem; color: var(--sd-muted); margin: 0; max-width: 60ch; }
.sda-empty { padding: 2rem 1rem; text-align: center; color: var(--sd-muted); background: #fff; border: 1px dashed var(--sd-border); border-radius: var(--sd-radius); }
.sda-spinner { display: inline-block; width: 26px; height: 26px; border: 3px solid var(--sd-border); border-top-color: var(--sd-accent); border-radius: 50%; animation: sda-spin 0.8s linear infinite; }
@keyframes sda-spin { to { transform: rotate(360deg); } }

.sda-filter { display: inline-flex; gap: 0.25rem; background: #fff; border: 1px solid var(--sd-border); border-radius: 10px; padding: 0.2rem; }
.sda-filter__tab { border: 0; background: transparent; padding: 0.4rem 0.8rem; border-radius: 8px; font-size: 0.8rem; font-weight: 600; color: var(--sd-muted); cursor: pointer; }
.sda-filter__tab.is-active { background: var(--sd-accent-soft); color: var(--sd-accent); }
.sda-filter__count { margin-left: 0.3rem; opacity: 0.8; }

.sda-grid {
  display: grid;
  /* min() so a column can never be wider than the space it has. */
  grid-template-columns: repeat(auto-fill, minmax(min(220px, 100%), 1fr));
  gap: 0.75rem;
}
.sda-badge {
  display: flex;
  gap: 0.75rem;
  /* Top-aligned, so a long name grows downwards and a short one leaves the
     space below it empty rather than floating the text off-centre. Every
     card in a row is the same height either way. */
  align-items: flex-start;
  background: #fff;
  border: 1px solid var(--sd-border);
  border-radius: var(--sd-radius);
  box-shadow: var(--sd-shadow);
  padding: 0.85rem;
  min-width: 0;
}
.sda-medal { width: 48px; height: 48px; flex-shrink: 0; }
.sda-medal__svg { width: 100%; height: 100%; }
.sda-badge__body { min-width: 0; flex: 1; }
.sda-badge__title {
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--sd-navy);
  margin: 0.3rem 0 0.15rem;
  line-height: 1.3;
  /* Course names run long, and they are what the badge is for: let them
     wrap in full rather than cutting them off. */
  overflow-wrap: anywhere;
}
.sda-badge__path { font-size: 0.75rem; color: var(--sd-muted); margin: 0; line-height: 1.35; overflow-wrap: anywhere; }
.sda-badge__date { font-size: 0.7rem; color: #94a3b8; margin: 0.35rem 0 0; }
.sda-link { color: var(--sd-accent); text-decoration: none; font-weight: 600; }
.sda-link:hover { text-decoration: underline; }

.sda-pill { display: inline-block; padding: 0.12rem 0.5rem; border-radius: 999px; font-size: 0.68rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; }
.sda-pill--module { background: var(--sd-accent-soft); color: var(--sd-accent); }
.sda-pill--pathway { background: #e0e7ff; color: var(--sd-navy); }
</style>
